import { FC, useState } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { useRouter } from 'next/router';
import SheetComponent from 'components/sheet';
import { Gtm } from 'tracking';
import { CatNutritionSegmentationIds } from 'constants/segmentation';
import { PetType } from 'constants/pet-type';
import { INITIAL_CAT_STATE } from 'stores/pet-profile-new/constants';
import { isEmptyProfile } from 'stores/pet-profile-new/utils/is-empty-profile';
import { useRootStore } from 'stores/use-root-store';
import { isCatProfile } from 'types/pet-profile';
import { CollectionProduct } from 'models/collection/collection';
import { PICKY_CATS_FUNNEL_CONTENT } from 'modules/picky-cats/content';
import { PickyCatsQuestion, PickyCatsTip } from 'modules/picky-cats/types';
import Question from 'modules/picky-cats/components/question';
import ResultWithTips from 'modules/picky-cats/components/result-with-tips';

interface PickyCatsFunnelProps {
  isOpen: boolean;
  onClose: () => void;
  variantName: string;
  location: string;
  products: CollectionProduct[];
  totalCount: number;
}

const RESULT_STEP = PICKY_CATS_FUNNEL_CONTENT.questions.length;
const PET_PROFILE_CAT_PATH = '/pet-profile/cat';
const PICKY_EATER_COLLECTION_PATH = '/collections/fur-katzen-wahlerisch';

const localizeQuestion = (intl: IntlShape, question: PickyCatsQuestion): PickyCatsQuestion => ({
  ...question,
  prompt: intl.formatMessage({
    id: `picky-cats:question:${question.id}:prompt`,
    defaultMessage: question.prompt,
  }),
  options: question.options.map((option) => ({
    ...option,
    label: intl.formatMessage({
      id: `picky-cats:question:${question.id}:option:${option.id}`,
      defaultMessage: option.label,
    }),
  })),
});

const localizeTips = (intl: IntlShape, tips: PickyCatsTip[]): PickyCatsTip[] =>
  tips.map((tip) => ({
    ...tip,
    text: intl.formatMessage({ id: `picky-cats:tip:${tip.id}:text`, defaultMessage: tip.text }),
  }));

/** Picky Cats overlay funnel: five questions then a tips result whose CTA sends visitors with a started pet profile to the cat pet profile and everyone else to the picky-eater collection. */
const PickyCatsFunnel: FC<PickyCatsFunnelProps> = ({
  isOpen,
  onClose,
  variantName,
  location,
  products,
  totalCount,
}) => {
  const intl = useIntl();
  const router = useRouter();
  const currentPetProfile = useRootStore((store) => store.currentPetProfile);
  const savePetProfile = useRootStore((store) => store.savePetProfile);
  const setPetType = useRootStore((store) => store.setPetType);

  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});

  const { questions, tips } = PICKY_CATS_FUNNEL_CONTENT;
  const isResult = step >= RESULT_STEP;
  const question = questions[step];
  const hasPetProfileData = !isEmptyProfile(currentPetProfile);

  const handleProductClick = (productId: string): void => {
    Gtm.track.selectElement({ element: ['pickycat', 'product', productId] });
  };

  const handleSelect = (optionId: string): void => {
    setAnswers((prev) => ({ ...prev, [question.id]: optionId }));
    Gtm.track.selectElement({ element: ['pickycat_answer', String(step + 1), optionId] });
  };

  const handleNext = (): void => {
    const nextStep = step + 1;
    setStep(nextStep);
    if (nextStep >= RESULT_STEP) {
      Gtm.track.viewElement({ element: ['pickycat', 'finish'] });
    }
  };

  const handleBack = (): void => setStep((prev) => Math.max(0, prev - 1));

  const handleRedirect = async (): Promise<void> => {
    Gtm.track.selectElement({ element: ['pickycat', 'finish'] });

    if (hasPetProfileData) {
      const catProfile = isCatProfile(currentPetProfile) ? currentPetProfile : INITIAL_CAT_STATE;
      setPetType(PetType.Cat);
      await savePetProfile({
        ...catProfile,
        selectedNeeds: [CatNutritionSegmentationIds.CatPickyEater],
      });
    }

    await router.push(hasPetProfileData ? PET_PROFILE_CAT_PATH : PICKY_EATER_COLLECTION_PATH);
  };

  return (
    <SheetComponent isOpen={isOpen} onClose={onClose}>
      <div data-test="picky-cats-funnel" data-variant={variantName} data-location={location}>
        {isResult ? (
          <ResultWithTips
            title={intl.formatMessage({ id: 'picky-cats:result:tips-title' })}
            subtitle={intl.formatMessage({ id: 'picky-cats:result:tips-subtitle' })}
            tips={localizeTips(intl, tips)}
            products={products}
            totalCount={totalCount}
            ctaLabel={intl.formatMessage({
              id: hasPetProfileData ? 'picky-cats:cta:see-all' : 'picky-cats:cta:browse-collection',
            })}
            onCtaClick={handleRedirect}
            onProductClick={handleProductClick}
          />
        ) : (
          <Question
            question={localizeQuestion(intl, question)}
            currentIndex={step}
            totalSteps={RESULT_STEP}
            selectedOptionId={answers[question.id]}
            onSelect={handleSelect}
            onBack={step > 0 ? handleBack : undefined}
            onNext={handleNext}
          />
        )}
      </div>
    </SheetComponent>
  );
};

export default PickyCatsFunnel;
